"use client";

import {
  backCellOrder,
  type DuplexFlip,
  type Flashcard,
  paginate,
} from "@/lib/tool-kit/study/flashcards";

/*
 * The flashcard maker's printable pages: a grid of fronts with dashed cut lines, then
 * the matching backs mirrored for double-sided printing (see `backCellOrder`). Hidden
 * on screen; on paper they are the whole page. Card text is the reader's own; there is
 * no interface text here.
 */

type Face = "term" | "definition";
export type Paper = "letter" | "a4";
export type Layout = "6" | "8" | "10";
export type Sides = "both" | "fronts";

export const ROWS: Record<Layout, number> = { "6": 3, "8": 4, "10": 5 };
export const COLUMNS = 2;
/** The printable area inside each paper's margins. */
export const SHEET: Record<
  Paper,
  { page: string; margin: string; width: string; height: string }
> = {
  letter: { page: "letter", margin: "0.5in", width: "7.5in", height: "10in" },
  a4: { page: "A4", margin: "10mm", width: "190mm", height: "277mm" },
};

/** Smaller type for longer text, so a long definition still fits its card. */
function fontSize(text: string, face: Face): string {
  const base = face === "term" ? 1.15 : 1;
  if (text.length <= 40) {
    return `${16 * base}pt`;
  }
  if (text.length <= 120) {
    return `${12 * base}pt`;
  }
  if (text.length <= 240) {
    return `${10 * base}pt`;
  }
  return `${8.5 * base}pt`;
}

function CardCell({
  text,
  face,
  cut,
}: {
  text?: string;
  face: Face;
  cut: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-center overflow-hidden p-[6mm] text-center text-black leading-snug ${cut ? "border-gray-400 border-r border-b border-dashed" : ""} ${face === "term" ? "font-semibold" : ""}`}
      style={{ fontSize: text ? fontSize(text, face) : undefined }}
    >
      {text ?? ""}
    </div>
  );
}

/** The printable pages: hidden on screen, the only thing on paper. */
export function PrintSheets({
  cards,
  paper,
  layout,
  flip,
  sides,
}: {
  cards: readonly Flashcard[];
  paper: Paper;
  layout: Layout;
  flip: DuplexFlip;
  sides: Sides;
}) {
  const rows = ROWS[layout];
  const pages = paginate(cards, COLUMNS * rows);
  const backOrder = backCellOrder(COLUMNS, rows, flip);
  const sheetStyle = {
    width: SHEET[paper].width,
    height: SHEET[paper].height,
    gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`,
    gridTemplateRows: `repeat(${rows}, 1fr)`,
  };
  const cells = Array.from({ length: COLUMNS * rows }, (_, index) => index);
  return (
    <div aria-hidden="true" className="hidden print:block">
      {pages.map((page, pageIndex) => {
        const last = pageIndex === pages.length - 1;
        const frontBreak = sides === "both" || !last ? "break-after-page" : "";
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: printed pages are positional
          <div key={pageIndex}>
            <div
              className={`grid border-gray-400 border-t border-l border-dashed ${frontBreak}`}
              style={sheetStyle}
            >
              {cells.map((cell) => (
                <CardCell cut face="term" key={cell} text={page[cell]?.term} />
              ))}
            </div>
            {sides === "both" ? (
              <div
                className={`grid ${last ? "" : "break-after-page"}`}
                style={sheetStyle}
              >
                {cells.map((cell) => (
                  <CardCell
                    cut={false}
                    face="definition"
                    key={cell}
                    text={page[backOrder[cell]]?.definition}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
